import React, { forwardRef } from 'react';
import { FormControl as MuiFormControl } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * This Component is a wrapper on MUI component.
 * This component should be imported and not MUI directly!
 *
 * Explicit FormControl props based on actual usage in the codebase.
 */
export interface FormControlProps {
  /** The content of the form control (labels, controls, helper text). */
  children?: React.ReactNode;
  /** The component used for the root node (e.g. `'fieldset'`). */
  component?: React.ElementType;
  /** If `true`, the descendant label and helper text are displayed in an error state. */
  error?: boolean;
  /** CSS class name applied to the root element. */
  className?: string;
}

// ref targets the default `div` root; `component='fieldset'` swaps the element at runtime only
const FormControl = forwardRef<HTMLDivElement, FormControlProps>(
  ({ children, component, error, className, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to FormControl: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the FormControlProps interface and to the component's signature.`,
        true
      );
    }
    // `component` is spread (not passed explicitly) because MUI v4's polymorphic overloads
    // reject an explicit `component` prop typed `ElementType | undefined`.
    return (
      <MuiFormControl error={error} className={className} ref={ref} {...{ component }} {...restProps}>
        {children}
      </MuiFormControl>
    );
  }
);

FormControl.displayName = 'FormControl';

export default FormControl;
